import React, { useState, useEffect, useRef, useCallback } from "react";
import { FieldArray, useFormikContext } from "formik";
import styles from "./style.module.scss";
import TextInput from "../../../../../../components/form/text-input";
import Select from "../../../../../../components/form/select";
import { US_STATES } from "../../../../../../../constants";
import CaretDown from "../../../../../../assets/svg/icons/common/CaretDown";
import baseStyles from "../../style.module.scss";
import baseLayoutStyles from "../../base.module.scss";
import FancyRadio from "../../../../../../components/form/fancy-radio-input";
import HeadingWithLine from "../../../../../../components/HeadingWithLine/HeadingWithLine";
import InfoIcon from "../../../../../../assets/svg/icons/common/info";
import Button from "../../../../../../components/new-button";
import PlusIcon from "../../../../../../assets/svg/icons/common/Plus";
import { getCountries } from "../../../../../../../api";
import { useEMSValidation } from "../../../../../../hooks/useEMSValidation";
import useDomainStore from "../../../../../../stores/domain";
import AddressAutocomplete from "../../../../../../components/form/address-autocomplete";
import { googlePlaceToFormData } from "../../../../../../utils/address-parser";
import ReactTooltip from "react-tooltip";
import NumberInput from "../../../../../../components/form/number-input";
import Spinner from "../../../../../../components/Loaders/Spinner";
import useSalesFormStore from "../../../../../../stores/sales-form";
import usePlaidIdv from "../../../../../../components/PlaidIdv/usePlaidIdv";
import VerifyIdButton from "../../../../../../components/PlaidIdv/VerifyIdButton";

const idTypeOptions = [
  { value: "LOCAL_STATE_ID", label: "Driver's License" },
  { value: "PASSPORT", label: "Passport" },
  { value: "STATE_ID", label: "State ID" },
];

const ToolTipSocialSecurity =
  "Do not add dashes. Please make sure your credit isn’t frozen or blocked before submitting this form.";
const ToolTipBirthDate = "You must be 18 years or older to apply";
const ToolTipOwnershipPercentage =
  "Only people with at least 25% ownership need to be included on this application.";

const titleOptions = [
  { value: 1, label: "President" },
  { value: 2, label: "Vice President" },
  { value: 3, label: "Secretary" },
  { value: 4, label: "Sole Proprietor" },
  { value: 5, label: "Treasurer" },
  { value: 6, label: "Partner" },
  { value: 7, label: "Member" },
  { value: 8, label: "CFO" },
  { value: 9, label: "Owner" },
  { value: 10, label: "CEO" },
  { value: 11, label: "Recorder" },
];

/**
 * PersonalInformation Component
 * Renders a form for collecting personal information including contact details, address, and additional owners
 *
 * @param {Object} props - Component props
 */
const PersonalInformation = () => {
  const { isKurvDomain } = useDomainStore();
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldError,
    setFieldTouched,
  } = useFormikContext();
  const {
    validateDrivingLicense,
    validatePhone,
    validatingPhone,
    validatingDrivingLicense,
    validationStatuses,
    setValidationStatus,
  } = useEMSValidation();
  const [usStateList, setUsStateList] = useState([]);
  const [additionalOwnersOpen, setAdditionalOwnersOpen] = useState({});
  const [countries, setCountries] = useState([]);
  const mountedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  // Plaid ID verification — one session at a time across every owner
  const editSlug = useSalesFormStore((state) => state.editSlug);
  const otpCode = useSalesFormStore((state) => state.otpCode);
  const idvRows = useSalesFormStore(
    (state) => state.applicationData?.powerapp_plaid_idv,
  );
  const idv = usePlaidIdv({
    editslug: editSlug,
    otpCode,
    isKurvDomain,
    rows: idvRows,
  });

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Initialize US state list options on component mount
   */
  useEffect(() => {
    const stateOptions = Object.entries(US_STATES).map(([, value]) => ({
      label: value,
      value: value,
    }));
    setUsStateList(stateOptions);

    getCountries().then((res) => {
      setCountries(res.data);
    });
  }, []);

  /**
   * Handles the "Same as" radio button changes
   * Copies address information based on selection
   * @param {string} value - Selected option ('same_as_no', 'same_as_dba', or 'same_as_legal')
   */
  const handleSameAsChange = (value) => {
    setFieldValue("personal_same_as_dba_legal", value);
    const dbaFieldMap = {
      field_personalstreet: "dba_address",
      field_personalcity: "dba_city",
      field_personalstate: "dba_state",
      field_personalzip: "dba_zip",
      personal_phone: "business_phone",
      personal_email: "business_email",
      field_residenceyears: "location_years",
      field_residencemonths: "location_months",
    };

    if (value === "dba") {
      Object.entries(dbaFieldMap).forEach(([key, value]) => {
        const valueToSet =
          value === "dba_state"
            ? US_STATES[values[value]] || values[value] || ""
            : values[value];
        setFieldValue(key, valueToSet);
        setFieldError(key, null);
      });

      setTimeout(() => {
        Object.entries(dbaFieldMap).forEach(([field]) => {
          setFieldTouched(field, true, true);
        });

        validatePhone(
          values.business_phone,
          "personal_phone",
          setFieldError,
          setFieldTouched,
        );
      }, 0);
    } else if (value === "business") {
      const legalFieldMap = {
        field_personalstreet: "legal_address",
        field_personalcity: "legal_city",
        field_personalstate: "legal_state",
        field_personalzip: "legal_zip",
        personal_phone: "legal_phone",
        personal_email: "business_email",
        field_residenceyears: "ownership_years",
        field_residencemonths: "ownership_months",
      };

      Object.entries(legalFieldMap).forEach(([key, value]) => {
        const valueToSet =
          value === "legal_state"
            ? US_STATES[values[value]] || values[value] || ""
            : values[value];
        setFieldValue(key, valueToSet);
        setFieldError(key, null);
      });

      setTimeout(() => {
        Object.entries(legalFieldMap).forEach(([field]) => {
          setFieldTouched(field, true, true);
        });

        validatePhone(
          values.legal_phone,
          "personal_phone",
          setFieldError,
          setFieldTouched,
        );
      }, 0);
    } else if (value === "no") {
      Object.keys(dbaFieldMap).forEach((key) => {
        setFieldValue(key, "");
        setFieldError(key, null);
      });

      setTimeout(() => {
        Object.keys(dbaFieldMap).forEach((field) => {
          setFieldTouched(field, false, false);
        });
      }, 0);
    }
  };

  const handleStateChange = async (stateValue, index = null) => {
    const fieldName =
      index !== null
        ? `additional_owners.${index}.signer_drivers_license_number`
        : "field_documentnumber";

    const licenseNumber =
      index !== null
        ? values.additional_owners[index].signer_drivers_license_number
        : values.field_documentnumber;

    const documentType =
      index !== null
        ? values.additional_owners[index].signer_document_type
        : values.field_documenttype;

    await validateDrivingLicense(
      licenseNumber,
      stateValue,
      documentType,
      fieldName,
      setFieldError,
      setFieldTouched,
    );
  };

  const handleDocumentTypeChange = async (documentType, index = null) => {
    const fieldName =
      index !== null
        ? `additional_owners.${index}.signer_drivers_license_number`
        : "field_documentnumber";

    if (documentType !== "LOCAL_STATE_ID") {
      setValidationStatus(fieldName, true);
      return;
    }

    const licenseNumber =
      index !== null
        ? values.additional_owners[index].signer_drivers_license_number
        : values.field_documentnumber;

    const state =
      index !== null
        ? values.additional_owners[index].signer_drivers_license_state
        : values.field_driverstate;

    await validateDrivingLicense(
      licenseNumber,
      state,
      documentType,
      fieldName,
      setFieldError,
      setFieldTouched,
    );
  };

  const debouncedValidatePhone = useCallback(
    (value, name) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        if (!mountedRef.current) return;
        if (validatingPhone?.[name]) return;

        await validatePhone(value, name, setFieldError, setFieldTouched);
      }, 500);
    },
    [validatingPhone, validatePhone, setFieldError, setFieldTouched],
  );

  const handlePhoneChange = async (event, index = null) => {
    const fieldName =
      index !== null
        ? `additional_owners.${index}.signer_phone`
        : event.target.name;
    const { value } = event.target;

    handleChange({
      target: {
        name: fieldName,
        value: value,
      },
    });
    if (!mountedRef.current) return;

    if (validatingPhone[fieldName]) return;

    debouncedValidatePhone(value, fieldName);
  };

  const handlePhoneBlur = async (event, index = null) => {
    const fieldName =
      index !== null
        ? `additional_owners.${index}.signer_phone`
        : event.target.name;
    const { value } = event.target;

    handleBlur({
      target: {
        name: fieldName,
        value: value,
      },
    });
    if (!mountedRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    if (validatingPhone[fieldName]) return;

    await validatePhone(value, fieldName, setFieldError, setFieldTouched);
  };

  const debouncedValidateDriverLicense = useCallback(
    (licenseNumber, state, documentType, fieldName) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(async () => {
        if (!mountedRef.current) return;
        if (validatingDrivingLicense?.[fieldName]) return;

        await validateDrivingLicense(
          licenseNumber,
          state,
          documentType,
          fieldName,
          setFieldError,
          setFieldTouched,
        );
      }, 500);
    },
    [
      validatingDrivingLicense,
      validateDrivingLicense,
      setFieldError,
      setFieldTouched,
    ],
  );

  const handleDriverLicenseChange = async (event, index = null) => {
    handleChange(event);
    if (!mountedRef.current) return;

    const { value } = event.target;
    const fieldName =
      index !== null
        ? `additional_owners.${index}.signer_drivers_license_number`
        : "field_documentnumber";

    const state =
      index !== null
        ? values.additional_owners[index].signer_drivers_license_state
        : values.field_driverstate;

    const documentType =
      index !== null
        ? values.additional_owners[index].signer_document_type
        : values.field_documenttype;

    debouncedValidateDriverLicense(value, state, documentType, fieldName);
  };

  const handleDriverLicenseBlur = async (event, index = null) => {
    handleBlur(event);
    if (!mountedRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    const { value } = event.target;
    const fieldName =
      index !== null
        ? `additional_owners.${index}.signer_drivers_license_number`
        : "field_documentnumber";

    if (validatingDrivingLicense?.[fieldName]) return;

    const state =
      index !== null
        ? values.additional_owners[index].signer_drivers_license_state
        : values.field_driverstate;

    const documentType =
      index !== null
        ? values.additional_owners[index].signer_document_type
        : values.field_documenttype;

    await validateDrivingLicense(
      value,
      state,
      documentType,
      fieldName,
      setFieldError,
      setFieldTouched,
    );
  };

  /**
   * Toggles the visibility of an additional owner's details section
   * @param {number} index - Index of the owner to toggle
   */
  const toggleAdditionalOwner = (index) => {
    setAdditionalOwnersOpen((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  /**
   * Adds a new owner to the form
   */
  const createNewOwner = () => ({
    signer_title: "",
    signer_first_name: "",
    signer_last_name: "",
    signer_email: "",
    signer_phone: "",
    signer_address: "",
    signer_city: "",
    signer_state: "",
    signer_zip: "",
    signer_country: "",
    signer_social_security: "",
    signer_drivers_license_number: "",
    signer_drivers_license_state: "",
    signer_date_of_birth: "",
    signer_document_type: "",
    signer_document_country: "",
    signer_ownership_percentage: "",
    signer_residence_years: "",
    signer_residence_months: "",
  });

  /**
   * Handles adding a new owner using FieldArray helpers
   * @param {Function} push - FieldArray push helper
   */
  const handleAddOwner = (push) => {
    push(createNewOwner());
    setAdditionalOwnersOpen((prev) => ({
      ...prev,
      [values.additional_owners?.length || 0]: true,
    }));
  };

  /**
   * Handles removing an owner using FieldArray helpers
   * @param {number} index - Index of the owner to remove
   * @param {Function} remove - FieldArray remove helper
   */
  const handleRemoveOwner = (index, remove) => {
    remove(index);

    const newOpenStates = {};
    Object.keys(additionalOwnersOpen).forEach((key) => {
      const keyNum = parseInt(key);
      if (keyNum < index) {
        newOpenStates[keyNum] = additionalOwnersOpen[keyNum];
      } else if (keyNum > index) {
        newOpenStates[keyNum - 1] = additionalOwnersOpen[keyNum];
      }
    });
    setAdditionalOwnersOpen(newOpenStates);
  };

  // Add function to handle address selection from Google Place Autocomplete
  const handlePersonalAddressSelected = (place) => {
    // Parse the Google place data
    const addressData = googlePlaceToFormData(place, {
      streetAddress: "field_personalstreet",
      city: "field_personalcity",
      state: "field_personalstate",
      zipCode: "field_personalzip",
    });

    // Special handling for state to ensure it matches the dropdown options
    if (addressData.field_personalstate) {
      // Find matching state in the state list options
      const matchingState = usStateList.find(
        (option) =>
          option.label === addressData.field_personalstate ||
          option.value === addressData.field_personalstate,
      );

      if (matchingState) {
        addressData.field_personalstate = matchingState.value;
      } else {
        console.log(
          "No matching state found in dropdown for:",
          addressData.field_personalstate,
        );
      }
    }

    // Update the form fields
    Object.entries(addressData).forEach(([field, value]) => {
      setFieldValue(field, value);
    });
  };

  // Function to handle address selection for additional owners
  const handleAdditionalOwnerAddressSelected = (place, index) => {
    // Parse the Google place data
    const addressData = googlePlaceToFormData(place, {
      address: "address",
      city: "city",
      state: "state",
      zipCode: "zip",
    });

    // Special handling for state to ensure it matches the dropdown options
    if (addressData.state) {
      // Find matching state in the state list options
      const matchingState = usStateList.find(
        (option) =>
          option.label === addressData.state ||
          option.value === addressData.state,
      );

      if (matchingState) {
        addressData.state = matchingState.value;
      } else {
        console.log(
          "No matching state found in dropdown for:",
          addressData.state,
        );
      }
    }

    // Update the nested form fields for the owner at the given index
    Object.entries(addressData).forEach(([field, value]) => {
      const ownerField = `additional_owners.${index}.signer_${field}`;
      setFieldValue(ownerField, value);
    });
  };

  return (
    <div className={baseStyles.sectionContainer}>
      <ReactTooltip className={styles.tooltip} />

      <div className={baseStyles.sectionHeader}>
        <h6 className={baseStyles.sectionTitle}>Personal Information</h6>
      </div>

      <div className={baseStyles.sectionFormContent}>
        <div className={styles.radioGroupContainer}>
          <div className={styles.radioGroupTitle}>
            <p className={styles.subtitle}>
              Personal Information the same as your DBA or Legal
            </p>
          </div>

          <div className={styles.radioGroup}>
            <FancyRadio
              label="No"
              name="personal_same_as_dba_legal"
              id="personal_same_as_no"
              className={styles.radioItem}
              checked={values.personal_same_as_dba_legal === "no"}
              onChange={() => handleSameAsChange("no")}
              error={
                touched.personal_same_as_dba_legal &&
                errors.personal_same_as_dba_legal
              }
            />

            <FancyRadio
              label="Same as DBA"
              name="personal_same_as_dba_legal"
              id="personal_same_as_dba"
              className={styles.radioItem}
              checked={values.personal_same_as_dba_legal === "dba"}
              onChange={() => handleSameAsChange("dba")}
              error={
                touched.personal_same_as_dba_legal &&
                errors.personal_same_as_dba_legal
              }
            />

            <FancyRadio
              label="Same as Legal"
              name="personal_same_as_dba_legal"
              id="personal_same_as_legal"
              className={styles.radioItem}
              checked={values.personal_same_as_dba_legal === "business"}
              onChange={() => handleSameAsChange("business")}
              error={
                touched.personal_same_as_dba_legal &&
                errors.personal_same_as_dba_legal
              }
            />
          </div>
          {touched.personal_same_as_dba_legal &&
            errors.personal_same_as_dba_legal && (
              <div className={styles.error}>
                {errors.personal_same_as_dba_legal}
              </div>
            )}
        </div>

        {/* Title Field */}
        <div className={styles.formGroup}>
          <Select
            label="Title"
            placeholder="Select Title"
            id="title"
            name="title"
            options={titleOptions}
            search
            value={values?.title ? +values.title : ""}
            onChange={(value) => {
              setFieldValue("title", value);
            }}
            onBlur={handleBlur}
            error={errors.title}
            touched={touched.title}
            errored={errors.title}
          />
        </div>

        {/* Name Fields */}
        <div className={styles.formGroup}>
          <TextInput
            label={"First Name"}
            placeholder={"First Name"}
            type="text"
            id="personal_first_name"
            name="personal_first_name"
            value={values.personal_first_name}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.personal_first_name}
            errored={errors.personal_first_name}
            error={errors.personal_first_name}
          />

          <TextInput
            label={"Last Name"}
            placeholder={"Last Name"}
            type="text"
            id="personal_last_name"
            name="personal_last_name"
            value={values.personal_last_name}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.personal_last_name}
            errored={errors.personal_last_name}
            error={errors.personal_last_name}
          />
        </div>

        {/* Phone and Email */}
        <div className={styles.formGroup}>
          <TextInput
            label={"Personal Phone"}
            placeholder={"Personal Phone"}
            type="text"
            id="personal_phone"
            name="personal_phone"
            value={values.personal_phone}
            suffix={
              validatingPhone?.personal_phone ? <Spinner size={16} /> : null
            }
            onBlur={(e) => handlePhoneBlur(e)}
            touched={touched.personal_phone}
            errored={
              errors.personal_phone ||
              validationStatuses?.personal_phone === false
            }
            error={
              errors.personal_phone ||
              validationStatuses?.personal_phone === false
                ? "* Enter a valid phone number"
                : null
            }
            disabled={
              values.personal_same_as_dba_legal === "dba" ||
              values.personal_same_as_dba_legal === "business"
            }
            cleaveOptions={{
              blocks: [3, 3, 4],
              delimiter: "-",
              numericOnly: true,
              onValueChanged: (e) => {
                handlePhoneChange(e);
              },
            }}
          />

          <TextInput
            label={"Personal Email"}
            placeholder={"Personal Email"}
            type="text"
            id="personal_email"
            name="personal_email"
            value={values.personal_email}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.personal_email}
            errored={errors.personal_email}
            error={errors.personal_email}
            disabled={
              values.personal_same_as_dba_legal === "dba" ||
              values.personal_same_as_dba_legal === "business"
            }
          />
        </div>

        {/* Address */}
        <div className={styles.formGroup}>
          <AddressAutocomplete
            label={"Personal Street Address"}
            placeholder={"Personal Street Address"}
            id="field_personalstreet"
            name="field_personalstreet"
            value={values.field_personalstreet}
            onChange={handleChange}
            onBlur={handleBlur}
            onPlaceSelected={handlePersonalAddressSelected}
            touched={touched.field_personalstreet}
            errored={errors.field_personalstreet}
            error={errors.field_personalstreet}
            disabled={
              values.personal_same_as_dba_legal === "dba" ||
              values.personal_same_as_dba_legal === "business"
            }
          />
        </div>

        <div className={styles.addressGrid}>
          <TextInput
            label={"City"}
            placeholder={"City"}
            type="text"
            id="field_personalcity"
            name="field_personalcity"
            value={values.field_personalcity}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.field_personalcity}
            errored={errors.field_personalcity}
            error={errors.field_personalcity}
            disabled={
              values.personal_same_as_dba_legal === "dba" ||
              values.personal_same_as_dba_legal === "business"
            }
          />

          <div>
            <Select
              label="State"
              placeholder="Select State"
              id="field_personalstate"
              name="field_personalstate"
              options={usStateList}
              search
              value={values.field_personalstate || ""}
              onChange={(value) => {
                setFieldValue("field_personalstate", value);
              }}
              onBlur={handleBlur}
              error={errors.field_personalstate}
              touched={touched.field_personalstate}
              errored={errors.field_personalstate}
              disabled={
                values.personal_same_as_dba_legal === "dba" ||
                values.personal_same_as_dba_legal === "business"
              }
            />
          </div>

          <TextInput
            label={"Zip"}
            placeholder={"Zip"}
            type="text"
            id="field_personalzip"
            name="field_personalzip"
            onBlur={handleBlur}
            value={values.field_personalzip}
            touched={touched.field_personalzip}
            errored={errors.field_personalzip}
            error={errors.field_personalzip}
            disabled={
              values.personal_same_as_dba_legal === "dba" ||
              values.personal_same_as_dba_legal === "business"
            }
            cleaveOptions={{
              blocks: [5],
              delimiter: "",
              numericOnly: true,
              onValueChanged: (e) => {
                handleChange({
                  target: { name: "field_personalzip", value: e.target.value },
                });
              },
            }}
          />
        </div>

        {/* Years of Residence */}
        <div className={styles.formGroup}>
          <div className={styles.residenceGroup}>
            <NumberInput
              label="Years of Residence"
              placeholder="0"
              type="text"
              id="field_residenceyears"
              name="field_residenceyears"
              min={0}
              max={99}
              value={values.field_residenceyears}
              onBlur={handleBlur}
              onChange={(value) => setFieldValue("field_residenceyears", value)}
              suffix="Years"
              error={errors.field_residenceyears}
              touched={touched.field_residenceyears}
              errored={errors.field_residenceyears}
            />

            <NumberInput
              label="Months of Residence"
              placeholder="0"
              type="text"
              id="field_residencemonths"
              name="field_residencemonths"
              min={0}
              max={11}
              value={values.field_residencemonths}
              onBlur={handleBlur}
              onChange={(value) =>
                setFieldValue("field_residencemonths", value)
              }
              suffix="Months"
              error={errors.field_residencemonths}
              touched={touched.field_residencemonths}
              errored={errors.field_residencemonths}
            />
          </div>
        </div>

        {/* ID Document Section */}
        <div className={styles.documentSection}>
          <HeadingWithLine text="IDENTITY DOCUMENT" margin={false} />

          <VerifyIdButton
            idv={idv}
            loadingKey="primary"
            fields={{
              first_name: "personal_first_name",
              last_name: "personal_last_name",
              email: "personal_email",
            }}
          />

          <div className={styles.addressGrid}>
            <div>
              <Select
                label="ID Type"
                placeholder="ID Type"
                id="field_documenttype"
                name="field_documenttype"
                options={idTypeOptions}
                value={values?.field_documenttype}
                onBlur={handleBlur}
                onChange={(value) => {
                  handleChange({
                    target: { name: "field_documenttype", value },
                  });
                  handleDocumentTypeChange(value);
                }}
                errored={errors.field_documenttype}
                touched={touched.field_documenttype}
                error={errors.field_documenttype}
              />
            </div>
            <TextInput
              label={"ID Number"}
              placeholder={"ID Number"}
              type="text"
              id="field_documentnumber"
              name="field_documentnumber"
              value={values.field_documentnumber}
              suffix={
                validatingDrivingLicense?.field_documentnumber ? (
                  <Spinner size={16} />
                ) : null
              }
              onChange={handleDriverLicenseChange}
              onBlur={handleDriverLicenseBlur}
              touched={
                touched.field_documentnumber ||
                validationStatuses?.field_documentnumber === false
              }
              errored={
                errors.field_documentnumber ||
                validationStatuses?.field_documentnumber === false
              }
              error={
                errors.field_documentnumber ||
                validationStatuses?.field_documentnumber === false
                  ? "* Enter a valid ID number"
                  : null
              }
            />

            {(values.field_documenttype === "LOCAL_STATE_ID" ||
              values.field_documenttype === "STATE_ID") && (
              <div>
                <Select
                  label="State"
                  placeholder="Select State"
                  id="field_driverstate"
                  name="field_driverstate"
                  options={usStateList}
                  search
                  value={values.field_driverstate || ""}
                  onChange={(value) => {
                    handleChange({
                      target: { name: "field_driverstate", value },
                    });
                    if (value) {
                      setFieldError("field_driverstate", undefined);
                    }
                    handleStateChange(value);
                  }}
                  onBlur={handleBlur}
                  error={errors.field_driverstate}
                  touched={touched.field_driverstate}
                  errored={errors.field_driverstate}
                />
              </div>
            )}

            {values.field_documenttype === "PASSPORT" && (
              <div>
                <Select
                  key={`field_documenttype_${countries?.length}`}
                  label="Country"
                  placeholder="Select Country"
                  id="document_country"
                  name="document_country"
                  options={countries}
                  search
                  value={values.document_country || ""}
                  onChange={(value) => {
                    setFieldValue("document_country", value);
                  }}
                  onBlur={handleBlur}
                  error={errors.document_country}
                  touched={touched.document_country}
                  errored={errors.document_country}
                />
              </div>
            )}
          </div>
          <div className={styles.addressGrid}>
            <TextInput
              label={"Social Security Number"}
              placeholder={"XXX-XX-XXXX"}
              type="text"
              id="field_socialsecurity"
              name="field_socialsecurity"
              onBlur={handleBlur}
              value={values.field_socialsecurity}
              touched={touched.field_socialsecurity}
              errored={errors.field_socialsecurity}
              error={errors.field_socialsecurity}
              suffix={
                <InfoIcon data-tip={ToolTipSocialSecurity} data-place="top" />
              }
              cleaveOptions={{
                blocks: [3, 2, 4],
                delimiter: "-",
                numericOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: {
                      name: "field_socialsecurity",
                      value: e.target.value,
                    },
                  });
                },
              }}
            />
            <TextInput
              label={"Date of Birth"}
              placeholder={"MM/DD/YYYY"}
              type="text"
              id="field_birthdate"
              name="field_birthdate"
              value={values.field_birthdate}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.field_birthdate}
              errored={errors.field_birthdate}
              error={errors.field_birthdate}
              suffix={<InfoIcon data-tip={ToolTipBirthDate} data-place="top" />}
              cleaveOptions={{
                date: true,
                datePattern: ["m", "d", "Y"],
                delimiter: "/",
                blocks: [2, 2, 4],
                onValueChanged: (e) => {
                  // Ensure the value is properly formatted
                  let value = e.target.value;
                  if (value && value.length === 8) {
                    value = value.replace(/(\d{2})(\d{2})(\d{4})/, "$1/$2/$3");
                  }
                  setFieldValue("field_birthdate", value);
                },
              }}
            />
          </div>
        </div>

        {/* Ownership Section */}
        <div className={styles.documentSection}>
          <HeadingWithLine text="OWNERSHIP" margin={false} />

          <div className={styles.ownershipGrid}>
            <NumberInput
              label="Ownership Percentage"
              placeholder="0"
              type="text"
              id="field_ownershippercentage"
              name="field_ownershippercentage"
              min={0}
              max={100}
              value={values.field_ownershippercentage}
              onChange={(value) =>
                setFieldValue("field_ownershippercentage", value)
              }
              onBlur={handleBlur}
              suffix={
                <>
                  <InfoIcon
                    data-tip={ToolTipOwnershipPercentage}
                    data-place="top"
                  />
                  <span className={baseLayoutStyles.ml8px}>%</span>
                </>
              }
              error={errors.field_ownershippercentage}
              touched={touched.field_ownershippercentage}
              errored={errors.field_ownershippercentage}
            />
          </div>
        </div>

        {/* Additional Owners */}
        <FieldArray name="additional_owners">
          {({ push, remove }) => (
            <>
              {values.additional_owners &&
                values.additional_owners.length > 0 &&
                values.additional_owners.map((owner, index) => (
                  <div key={index} className={styles.additionalOwner}>
                    <div
                      className={styles.additionalOwnerHeader}
                      onClick={() => toggleAdditionalOwner(index)}
                    >
                      <div className={styles.additionalOwnerTitle}>
                        <CaretDown
                          className={
                            additionalOwnersOpen[index]
                              ? styles.caretOpen
                              : styles.caretClosed
                          }
                        />
                        <span>ADDITIONAL OWNER #{index + 2}</span>
                      </div>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveOwner(index, remove);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M13.5 3H11V2.5C11 2.10218 10.842 1.72064 10.5607 1.43934C10.2794 1.15804 9.89782 1 9.5 1H6.5C6.10218 1 5.72064 1.15804 5.43934 1.43934C5.15804 1.72064 5 2.10218 5 2.5V3H2.5C2.36739 3 2.24021 3.05268 2.14645 3.14645C2.05268 3.24021 2 3.36739 2 3.5C2 3.63261 2.05268 3.75979 2.14645 3.85355C2.24021 3.94732 2.36739 4 2.5 4H3V13C3 13.2652 3.10536 13.5196 3.29289 13.7071C3.48043 13.8946 3.73478 14 4 14H12C12.2652 14 12.5196 13.8946 12.7071 13.7071C12.8946 13.5196 13 13.2652 13 13V4H13.5C13.6326 4 13.7598 3.94732 13.8536 3.85355C13.9473 3.75979 14 3.63261 14 3.5C14 3.36739 13.9473 3.24021 13.8536 3.14645C13.7598 3.05268 13.6326 3 13.5 3ZM6 2.5C6 2.36739 6.05268 2.24021 6.14645 2.14645C6.24021 2.05268 6.36739 2 6.5 2H9.5C9.63261 2 9.75979 2.05268 9.85355 2.14645C9.94732 2.24021 10 2.36739 10 2.5V3H6V2.5ZM12 13H4V4H12V13ZM7 6.5V10.5C7 10.6326 6.94732 10.7598 6.85355 10.8536C6.75979 10.9473 6.63261 11 6.5 11C6.36739 11 6.24021 10.9473 6.14645 10.8536C6.05268 10.7598 6 10.6326 6 10.5V6.5C6 6.36739 6.05268 6.24021 6.14645 6.14645C6.24021 6.05268 6.36739 6 6.5 6C6.63261 6 6.75979 6.05268 6.85355 6.14645C6.94732 6.24021 7 6.36739 7 6.5ZM10 6.5V10.5C10 10.6326 9.94732 10.7598 9.85355 10.8536C9.75979 10.9473 9.63261 11 9.5 11C9.36739 11 9.24021 10.9473 9.14645 10.8536C9.05268 10.7598 9 10.6326 9 10.5V6.5C9 6.36739 9.05268 6.24021 9.14645 6.14645C9.24021 6.05268 9.36739 6 9.5 6C9.63261 6 9.75979 6.05268 9.85355 6.14645C9.94732 6.24021 10 6.36739 10 6.5Z"
                            fill="#3A383F"
                          />
                        </svg>
                      </button>
                    </div>

                    {additionalOwnersOpen[index] && (
                      <div className={styles.additionalOwnerContent}>
                        {/* Owner form fields would go here - similar to main form */}

                        {/* Title Field */}
                        <div className={styles.formGroup}>
                          <Select
                            label="Title"
                            placeholder="Select Title"
                            id={`additional_owners.${index}.signer_title`}
                            name={`additional_owners.${index}.signer_title`}
                            options={titleOptions}
                            search
                            value={
                              owner?.signer_title ? +owner.signer_title : ""
                            }
                            onChange={(value) => {
                              setFieldValue(
                                `additional_owners.${index}.signer_title`,
                                value,
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              errors?.additional_owners?.[index]?.signer_title
                            }
                            touched={
                              touched?.additional_owners?.[index]?.signer_title
                            }
                            errored={
                              errors?.additional_owners?.[index]?.signer_title
                            }
                          />
                        </div>

                        <div className={styles.formGroup}>
                          <TextInput
                            label={"First Name"}
                            placeholder={"First Name"}
                            type="text"
                            id={`additional_owners.${index}.signer_first_name`}
                            name={`additional_owners.${index}.signer_first_name`}
                            value={owner.signer_first_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              errors?.additional_owners?.[index]
                                ?.signer_first_name
                            }
                            touched={
                              touched?.additional_owners?.[index]
                                ?.signer_first_name
                            }
                            errored={
                              errors?.additional_owners?.[index]
                                ?.signer_first_name
                            }
                          />

                          <TextInput
                            label={"Last Name"}
                            placeholder={"Last Name"}
                            type="text"
                            id={`additional_owners.${index}.signer_last_name`}
                            name={`additional_owners.${index}.signer_last_name`}
                            value={owner.signer_last_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              errors?.additional_owners?.[index]
                                ?.signer_last_name
                            }
                            touched={
                              touched?.additional_owners?.[index]
                                ?.signer_last_name
                            }
                            errored={
                              errors?.additional_owners?.[index]
                                ?.signer_last_name
                            }
                          />
                        </div>

                        <div className={styles.formGroup}>
                          <TextInput
                            label={"Personal Phone"}
                            placeholder={"Personal Phone"}
                            type="text"
                            id={`additional_owners.${index}.signer_phone`}
                            name={`additional_owners.${index}.signer_phone`}
                            value={values.additional_owners[index].signer_phone}
                            suffix={
                              validatingPhone?.[
                                `additional_owners.${index}.signer_phone`
                              ] ? (
                                <Spinner size={16} />
                              ) : null
                            }
                            onBlur={(e) => handlePhoneBlur(e, index)}
                            touched={
                              touched?.additional_owners?.[index]?.signer_phone
                            }
                            errored={
                              errors?.additional_owners?.[index]
                                ?.signer_phone ||
                              validationStatuses?.[
                                `additional_owners.${index}.signer_phone`
                              ] === false
                            }
                            error={
                              errors?.additional_owners?.[index]
                                ?.signer_phone ||
                              validationStatuses?.[
                                `additional_owners.${index}.signer_phone`
                              ] === false
                                ? "* Enter a valid phone number"
                                : null
                            }
                            cleaveOptions={{
                              blocks: [3, 3, 4],
                              delimiter: "-",
                              numericOnly: true,
                              onValueChanged: (e) => {
                                handlePhoneChange(e, index);
                              },
                            }}
                          />

                          <TextInput
                            label={"Personal Email"}
                            placeholder={"Personal Email"}
                            type="email"
                            id={`additional_owners.${index}.signer_email`}
                            name={`additional_owners.${index}.signer_email`}
                            value={owner.signer_email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              errors?.additional_owners?.[index]?.signer_email
                            }
                            touched={
                              touched?.additional_owners?.[index]?.signer_email
                            }
                            errored={
                              errors?.additional_owners?.[index]?.signer_email
                            }
                          />
                        </div>

                        <div className={styles.addressSection}>
                          <AddressAutocomplete
                            label={"Street Address"}
                            placeholder={"Street Address"}
                            id={`additional_owners.${index}.signer_address`}
                            name={`additional_owners.${index}.signer_address`}
                            value={owner.signer_address}
                            onChange={handleChange}
                            onPlaceSelected={(place) =>
                              handleAdditionalOwnerAddressSelected(place, index)
                            }
                          />
                        </div>

                        <div className={styles.addressGrid}>
                          <TextInput
                            label={"City"}
                            placeholder={"City"}
                            type="text"
                            id={`additional_owners.${index}.signer_city`}
                            name={`additional_owners.${index}.signer_city`}
                            value={owner.signer_city}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              errors?.additional_owners?.[index]?.signer_city
                            }
                            touched={
                              touched?.additional_owners?.[index]?.signer_city
                            }
                            errored={
                              errors?.additional_owners?.[index]?.signer_city
                            }
                          />
                          <Select
                            label="State"
                            placeholder="Select State"
                            id={`additional_owners.${index}.signer_state`}
                            name={`additional_owners.${index}.signer_state`}
                            options={usStateList}
                            search
                            value={owner.signer_state || ""}
                            onChange={(value) => {
                              setFieldValue(
                                `additional_owners.${index}.signer_state`,
                                value,
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              errors?.additional_owners?.[index]?.signer_state
                            }
                            touched={
                              touched?.additional_owners?.[index]?.signer_state
                            }
                            errored={
                              errors?.additional_owners?.[index]?.signer_state
                            }
                          />

                          <TextInput
                            label={"Zip"}
                            placeholder={"Zip"}
                            type="text"
                            id={`additional_owners.${index}.signer_zip`}
                            name={`additional_owners.${index}.signer_zip`}
                            value={owner.signer_zip}
                            onBlur={handleBlur}
                            error={
                              errors?.additional_owners?.[index]?.signer_zip
                            }
                            touched={
                              touched?.additional_owners?.[index]?.signer_zip
                            }
                            errored={
                              errors?.additional_owners?.[index]?.signer_zip
                            }
                            cleaveOptions={{
                              blocks: [5],
                              delimiter: "",
                              numericOnly: true,
                              onValueChanged: (e) => {
                                handleChange({
                                  target: {
                                    name: `additional_owners.${index}.signer_zip`,
                                    value: e.target.value,
                                  },
                                });
                              },
                            }}
                          />
                        </div>

                        <div className={styles.formGroup}>
                          <div className={styles.residenceGroup}>
                            <NumberInput
                              label="Years of Residence"
                              id={`additional_owners.${index}.signer_residence_years`}
                              name={`additional_owners.${index}.signer_residence_years`}
                              min={0}
                              max={99}
                              value={owner.signer_residence_years}
                              onChange={(value) =>
                                setFieldValue(
                                  `additional_owners.${index}.signer_residence_years`,
                                  value,
                                )
                              }
                              suffix="Years"
                              onBlur={handleBlur}
                              error={
                                errors?.additional_owners?.[index]
                                  ?.signer_residence_years
                              }
                              touched={
                                touched?.additional_owners?.[index]
                                  ?.signer_residence_years
                              }
                              errored={
                                errors?.additional_owners?.[index]
                                  ?.signer_residence_years
                              }
                            />

                            <NumberInput
                              label="Months of Residence"
                              id={`additional_owners.${index}.signer_residence_months`}
                              name={`additional_owners.${index}.signer_residence_months`}
                              min={0}
                              max={11}
                              value={owner.signer_residence_months}
                              onChange={(value) =>
                                setFieldValue(
                                  `additional_owners.${index}.signer_residence_months`,
                                  value,
                                )
                              }
                              suffix="Months"
                              onBlur={handleBlur}
                              error={
                                errors?.additional_owners?.[index]
                                  ?.signer_residence_months
                              }
                              touched={
                                touched?.additional_owners?.[index]
                                  ?.signer_residence_months
                              }
                              errored={
                                errors?.additional_owners?.[index]
                                  ?.signer_residence_months
                              }
                            />
                          </div>
                        </div>

                        {/* ID Document Section */}
                        <div className={styles.documentSection}>
                          <HeadingWithLine
                            text="IDENTITY DOCUMENT"
                            margin={false}
                          />

                          <VerifyIdButton
                            idv={idv}
                            loadingKey={`additional-${index}`}
                            fields={{
                              first_name: `additional_owners.${index}.signer_first_name`,
                              last_name: `additional_owners.${index}.signer_last_name`,
                              email: `additional_owners.${index}.signer_email`,
                            }}
                          />

                          <div className={styles.addressGrid}>
                            <div>
                              <Select
                                label="ID Type"
                                placeholder="ID Type"
                                id={`additional_owners.${index}.signer_document_type`}
                                name={`additional_owners.${index}.signer_document_type`}
                                options={idTypeOptions}
                                value={owner.signer_document_type}
                                onChange={(value) => {
                                  handleChange({
                                    target: {
                                      name: `additional_owners.${index}.signer_document_type`,
                                      value,
                                    },
                                  });
                                  handleDocumentTypeChange(value, index);
                                }}
                                onBlur={handleBlur}
                                error={
                                  errors?.additional_owners?.[index]
                                    ?.signer_document_type
                                }
                                touched={
                                  touched?.additional_owners?.[index]
                                    ?.signer_document_type
                                }
                                errored={
                                  errors?.additional_owners?.[index]
                                    ?.signer_document_type
                                }
                              />
                            </div>

                            <TextInput
                              label={"ID Number"}
                              placeholder={"ID Number"}
                              type="text"
                              id={`additional_owners.${index}.signer_drivers_license_number`}
                              name={`additional_owners.${index}.signer_drivers_license_number`}
                              value={owner.signer_drivers_license_number}
                              suffix={
                                validatingDrivingLicense?.[
                                  `additional_owners.${index}.signer_drivers_license_number`
                                ] ? (
                                  <Spinner size={16} />
                                ) : null
                              }
                              onChange={(e) =>
                                handleDriverLicenseChange(e, index)
                              }
                              onBlur={(e) => handleDriverLicenseBlur(e, index)}
                              touched={
                                touched?.additional_owners?.[index]
                                  ?.signer_drivers_license_number
                              }
                              errored={
                                errors?.additional_owners?.[index]
                                  ?.signer_drivers_license_number ||
                                validationStatuses?.[
                                  `additional_owners.${index}.signer_drivers_license_number`
                                ] === false
                              }
                              error={
                                errors?.additional_owners?.[index]
                                  ?.signer_drivers_license_number ||
                                validationStatuses?.[
                                  `additional_owners.${index}.signer_drivers_license_number`
                                ] === false
                                  ? "* Enter a valid ID number"
                                  : null
                              }
                            />

                            {owner.signer_document_type === "PASSPORT" && (
                              <div>
                                <Select
                                  label="Country"
                                  key={`additional_owners.${index}.signer_document_country-${countries?.length}`}
                                  placeholder="Select Country"
                                  id={`additional_owners.${index}.signer_document_country`}
                                  name={`additional_owners.${index}.signer_document_country`}
                                  options={countries}
                                  search
                                  value={owner.signer_document_country || ""}
                                  onChange={(value) => {
                                    setFieldValue(
                                      `additional_owners.${index}.signer_document_country`,
                                      value,
                                    );
                                  }}
                                  onBlur={handleBlur}
                                  error={
                                    errors?.additional_owners?.[index]
                                      ?.signer_document_country
                                  }
                                  touched={
                                    touched?.additional_owners?.[index]
                                      ?.signer_document_country
                                  }
                                  errored={
                                    errors?.additional_owners?.[index]
                                      ?.signer_document_country
                                  }
                                />
                              </div>
                            )}

                            {(owner.signer_document_type === "LOCAL_STATE_ID" ||
                              owner.signer_document_type === "STATE_ID") && (
                              <div>
                                <Select
                                  label="State"
                                  placeholder="Select State"
                                  id={`additional_owners.${index}.signer_drivers_license_state`}
                                  name={`additional_owners.${index}.signer_drivers_license_state`}
                                  options={usStateList}
                                  search
                                  value={
                                    owner.signer_drivers_license_state || ""
                                  }
                                  onChange={(value) => {
                                    handleChange({
                                      target: {
                                        name: `additional_owners.${index}.signer_drivers_license_state`,
                                        value,
                                      },
                                    });
                                    handleStateChange(value, index);
                                  }}
                                  onBlur={handleBlur}
                                  error={
                                    errors?.additional_owners?.[index]
                                      ?.signer_drivers_license_state
                                  }
                                  touched={
                                    touched?.additional_owners?.[index]
                                      ?.signer_drivers_license_state
                                  }
                                  errored={
                                    errors?.additional_owners?.[index]
                                      ?.signer_drivers_license_state
                                  }
                                />
                              </div>
                            )}
                          </div>
                          <div className={styles.addressGrid}>
                            <TextInput
                              label={"Social Security Number"}
                              placeholder={"XXX-XX-XXXX"}
                              type="text"
                              id={`additional_owners.${index}.signer_social_security`}
                              name={`additional_owners.${index}.signer_social_security`}
                              value={owner.signer_social_security}
                              onBlur={handleBlur}
                              error={
                                errors?.additional_owners?.[index]
                                  ?.signer_social_security
                              }
                              touched={
                                touched?.additional_owners?.[index]
                                  ?.signer_social_security
                              }
                              errored={
                                errors?.additional_owners?.[index]
                                  ?.signer_social_security
                              }
                              suffix={
                                <InfoIcon
                                  data-tip={ToolTipSocialSecurity}
                                  data-place="top"
                                />
                              }
                              cleaveOptions={{
                                blocks: [3, 2, 4],
                                delimiter: "-",
                                numericOnly: true,
                                onValueChanged: (e) => {
                                  handleChange({
                                    target: {
                                      name: `additional_owners.${index}.signer_social_security`,
                                      value: e.target.value,
                                    },
                                  });
                                },
                              }}
                            />
                            <TextInput
                              label={"Date of Birth"}
                              placeholder={"MM/DD/YYYY"}
                              type="text"
                              id={`additional_owners.${index}.signer_date_of_birth`}
                              name={`additional_owners.${index}.signer_date_of_birth`}
                              value={owner.signer_date_of_birth}
                              onBlur={handleBlur}
                              error={
                                errors?.additional_owners?.[index]
                                  ?.signer_date_of_birth
                              }
                              touched={
                                touched?.additional_owners?.[index]
                                  ?.signer_date_of_birth
                              }
                              errored={
                                errors?.additional_owners?.[index]
                                  ?.signer_date_of_birth
                              }
                              suffix={
                                <InfoIcon
                                  data-tip={ToolTipBirthDate}
                                  data-place="top"
                                />
                              }
                              cleaveOptions={{
                                date: true,
                                datePattern: ["m", "d", "Y"],
                                delimiter: "/",
                                blocks: [2, 2, 4],
                                onValueChanged: (e) => {
                                  handleChange({
                                    target: {
                                      name: `additional_owners.${index}.signer_date_of_birth`,
                                      value: e.target.value,
                                    },
                                  });
                                },
                              }}
                            />
                          </div>
                        </div>

                        {/* Ownership Section */}
                        <div className={styles.documentSection}>
                          <HeadingWithLine text="OWNERSHIP" margin={false} />

                          <div className={styles.ownershipGrid}>
                            <NumberInput
                              label="Ownership Percentage"
                              placeholder="0"
                              type="text"
                              id={`additional_owners.${index}.signer_ownership_percentage`}
                              name={`additional_owners.${index}.signer_ownership_percentage`}
                              min={0}
                              max={100}
                              value={owner.signer_ownership_percentage}
                              onChange={(value) =>
                                setFieldValue(
                                  `additional_owners.${index}.signer_ownership_percentage`,
                                  value,
                                )
                              }
                              onBlur={handleBlur}
                              suffix={
                                <>
                                  <InfoIcon
                                    data-tip={ToolTipOwnershipPercentage}
                                    data-place="top"
                                  />
                                  <span className={baseLayoutStyles.ml8px}>
                                    %
                                  </span>
                                </>
                              }
                              error={
                                errors?.additional_owners?.[index]
                                  ?.signer_ownership_percentage
                              }
                              touched={
                                touched?.additional_owners?.[index]
                                  ?.signer_ownership_percentage
                              }
                              errored={
                                errors?.additional_owners?.[index]
                                  ?.signer_ownership_percentage
                              }
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

              {/* Add Owner Button */}
              <div className={styles.addOwnerButton}>
                <Button
                  onClick={() => handleAddOwner(push)}
                  variant={"ghost"}
                  icon={
                    <PlusIcon color={isKurvDomain ? "#22A276" : "#4C70EF"} />
                  }
                  type="button"
                >
                  Add Owner
                </Button>
              </div>
            </>
          )}
        </FieldArray>
      </div>

      {idv.launcher}
    </div>
  );
};

export default PersonalInformation;
